import React, { useState } from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import AddOutlined from "@mui/icons-material/AddOutlined";
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Input,
  Table,
  Dialog,
  Progress,
  Skeleton,
  Stat,
  StatGrid,
  ToastProvider,
  useToast,
} from "../../components/ds";
import ChartSkeleton from "../../components/OrgAdminGraph/ChartSkeleton";
import styles from "./UiKit.module.scss";

function Section({ title, children }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.h2}>{title}</h2>
      <div className={styles.row}>{children}</div>
    </section>
  );
}

function ToastDemo() {
  const toast = useToast();
  return (
    <>
      <Button variant="secondary" onClick={() => toast.info("Heads up", { description: "This is an info toast." })}>
        Info toast
      </Button>
      <Button variant="secondary" onClick={() => toast.success("Saved", { description: "Your changes were saved." })}>
        Success toast
      </Button>
      <Button variant="secondary" onClick={() => toast.error("Failed", { description: "Something went wrong." })}>
        Error toast
      </Button>
    </>
  );
}

export default function UiKit() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <ToastProvider>
      <div className={styles.page}>
        <header className={styles.header}>
          <Badge variant="info">DEV ONLY</Badge>
          <h1 className={styles.h1}>Cybrbase Design System — UI Kit</h1>
          <p className={styles.sub}>
            Primitive library preview. Not mounted in production builds.
          </p>
        </header>

        <Section title="Buttons">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="primary" leftIcon={<AddOutlined fontSize="inherit" />}>
            With icon
          </Button>
          <Button variant="primary" disabled>
            Disabled
          </Button>
        </Section>

        <Section title="Button sizes">
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </Section>

        <Section title="Badges">
          <Badge variant="neutral">PB-SEC-042</Badge>
          <Badge variant="info">Active</Badge>
          <Badge variant="success">Complete</Badge>
          <Badge variant="warning">Pending</Badge>
          <Badge variant="error">High Criticality</Badge>
        </Section>

        <Section title="Inputs">
          <Input placeholder="Entry field..." />
          <Input placeholder="Mono / technical" mono />
          <Input placeholder="Invalid" invalid />
          <Input placeholder="Disabled" disabled />
        </Section>

        <Section title="Progress">
          <div style={{ width: 240 }}>
            <Progress value={1} max={28} />
          </div>
          <div style={{ width: 240 }}>
            <Progress value={64} />
          </div>
        </Section>

        <Section title="Skeleton">
          <Skeleton width={120} height={26} />
          <Skeleton shape="line" width={200} />
          <Skeleton shape="circle" width={40} height={40} />
        </Section>

        <Section title="Stats (loaded vs loading)">
          <div style={{ width: "100%", maxWidth: 520 }}>
            <StatGrid>
              <Stat value="12" label="Assessments" />
              <Stat value="64%" label="Avg. domain progress" />
              <Stat value="3" label="Incomplete domains" loading />
            </StatGrid>
          </div>
        </Section>

        <Section title="Chart skeleton (reserved height)">
          <div style={{ width: "100%", maxWidth: 520 }}>
            <ChartSkeleton variant="line" />
          </div>
        </Section>

        <Section title="Cards">
          <Card>
            <CardHeader>
              <CardTitle>Static card</CardTitle>
            </CardHeader>
            <CardContent>White surface, 1px border, no shadow.</CardContent>
          </Card>
          <Card hoverable>
            <CardHeader>
              <CardTitle>Hoverable card</CardTitle>
            </CardHeader>
            <CardContent>Soft ambient shadow on hover.</CardContent>
            <CardFooter>
              <Button size="sm">Action</Button>
              <Button size="sm" variant="secondary">
                Cancel
              </Button>
            </CardFooter>
          </Card>
        </Section>

        <Section title="Table">
          <Table>
            <thead>
              <tr>
                <th>Session</th>
                <th>Scenario</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>SES-1001</td>
                <td>Lost or Stolen Laptop</td>
                <td>
                  <Badge variant="success">Complete</Badge>
                </td>
              </tr>
              <tr>
                <td>SES-1002</td>
                <td>Ransomware</td>
                <td>
                  <Badge variant="warning">In progress</Badge>
                </td>
              </tr>
            </tbody>
          </Table>
        </Section>

        <Section title="Dialog & Toasts">
          <Button onClick={() => setDialogOpen(true)}>Open dialog</Button>
          <ToastDemo />
        </Section>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Complete exercise?"
          footer={
            <>
              <Button variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setDialogOpen(false)}>Confirm</Button>
            </>
          }
        >
          <p style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <InfoOutlined fontSize="small" style={{ color: "var(--cb-primary)" }} />
            This marks the tabletop exercise as complete. You can reopen it later.
          </p>
        </Dialog>
      </div>
    </ToastProvider>
  );
}
